import { eColor, eStatus, fmtDate, pathName } from '@balansa/shared';
import { IconBolt, IconLink } from '../lib/icons';
import { useApp, useWheel } from '../store';
import { Tooltip } from './Tooltip';
import { Wheel } from './Wheel';

export function WheelCard() {
  return (
    <section className="card wheel-card" aria-label="Aplis">
      <Toolbar />
      <div className="svg-wrap" id="svg-wrap">
        <Chips />
        <Wheel />
        <Tooltip />
        <Battery />
      </div>
      <Legend />
      <details className="help">
        <summary>Kā lietot</summary>
        <ul>
          <li>Velc krāsainos punktus pa asi, lai mainītu vērtību: 0 centrā, 10 malā. Jomām ar apakšjomām vērtība ir to vidējais — maini apakšjomas.</li>
          <li>Uzbrauc uz jomas, lai redzētu, ko tā dod citām un ko saņem. Klikšķis jomu izvēlas un fokusē tās saites.</li>
          <li>“Visas apakšjomas” izvērš apli līdz zemākajam līmenim — robi kļūst redzami precīzāk.</li>
          <li>
            <b>Jauna saite:</b> velc no jomas nosaukuma (krāsainā josla) vai apakšjomas segmenta uz citu jomu un atlaid — atvērsies logs, kur izvēlies
            “dod” vai “atņem”, ko tieši un cik stipri. Režīmā “Zīmēt saites” var vilkt arī no punktiem vai vienkārši klikšķināt divas jomas pēc kārtas.
          </li>
          <li>Klikšķis uz bultas atver saiti labošanai vai dzēšanai. Saites var veidot arī cilnē “Saites” vai ar bultas pogu jomu sarakstā.</li>
          <li>
            <b>Enerģijas baterija</b> (augšā pa labi) rāda aptuveno enerģiju tagad: naktī to uzlādē miegs, dienā to patērē jomas, bet veselība var palielināt
            noturību. Visu regulē cilnē “Enerģija”.
          </li>
          <li>
            <b>Aktivitāšu kārtis</b> (zem apļa): izspēlē kārti, kad aktivitāte notikusi. Tā maina jomu vērtības un enerģiju, un iezīmētie nosacījumi parāda,
            kā tavs pašreizējais stāvoklis maina ietekmi.
          </li>
          <li>Visas izmaiņas tiek saglabātas serverī automātiski — aplis ir pieejams no jebkuras ierīces pēc pieteikšanās.</li>
          <li>Cilnē “Vēsture” ik pa laikam saglabā stāvokli un salīdzini, kā aplis mainās.</li>
        </ul>
      </details>
    </section>
  );
}

function Toolbar() {
  const settings = useApp((s) => s.wheel!.settings);
  const mode = useApp((s) => s.ui.mode);
  const setSettings = useApp((s) => s.setSettings);
  const setLinkMode = useApp((s) => s.setLinkMode);
  return (
    <div className="toolbar">
      <div className="seg" role="group" aria-label="Skats">
        {(
          [
            ['main', 'Galvenās jomas'],
            ['detail', 'Visas apakšjomas']
          ] as const
        ).map(([v, t]) => (
          <button key={v} type="button" aria-pressed={settings.view === v} onClick={() => setSettings({ view: v })}>
            {t}
          </button>
        ))}
      </div>
      <div className="seg" role="group" aria-label="Mijiedarbības">
        {(
          [
            ['all', 'Visas saites'],
            ['focus', 'Tikai izvēlētās'],
            ['none', 'Slēpt']
          ] as const
        ).map(([v, t]) => (
          <button key={v} type="button" aria-pressed={settings.links === v} onClick={() => setSettings({ links: v })}>
            {t}
          </button>
        ))}
      </div>
      <button
        type="button"
        className="btn sm toggle"
        aria-pressed={mode === 'link'}
        onClick={() => setLinkMode(mode !== 'link')}
        title="Velc no vienas jomas uz otru vai klikšķini divas jomas pēc kārtas"
      >
        <IconLink />
        Zīmēt saites
      </button>
      <span className="sp" />
      <label className="thresh" title="Trūkuma slieksnis: jomas ar vērtību līdz šim skaitlim tiek atzīmētas kā trūkums">
        Slieksnis{' '}
        <input type="range" min={0} max={10} step={1} value={settings.threshold} onChange={(e) => setSettings({ threshold: +e.target.value })} aria-label="Trūkuma slieksnis" />{' '}
        <b>{settings.threshold}</b>
      </label>
    </div>
  );
}

function Chips() {
  const { wheel, idx } = useWheel();
  const ui = useApp((s) => s.ui);
  const setUi = useApp((s) => s.setUi);
  const setLinkMode = useApp((s) => s.setLinkMode);
  const setSettings = useApp((s) => s.setSettings);
  const snap = wheel.settings.compare ? wheel.snapshots.find((s) => s.id === wheel.settings.compare) : null;
  return (
    <div className="chips">
      {ui.mode === 'link' ? (
        <span className="chipx info">
          {ui.linkSrc && idx.node.has(ui.linkSrc) ? (
            <span>
              No <b>{idx.node.get(ui.linkSrc)!.name}</b> — tagad klikšķini mērķa jomu
            </span>
          ) : (
            'Velc no jomas uz jomu vai klikšķini divas pēc kārtas'
          )}
          <button type="button" aria-label="Beigt zīmēt saites" title="Beigt (Esc)" onClick={() => setLinkMode(false)}>
            ×
          </button>
        </span>
      ) : null}
      {ui.selected && idx.node.has(ui.selected) ? (
        <span className="chipx" data-testid="chip-selected">
          <i style={{ background: idx.color(ui.selected) }} />
          Izvēlēta: {pathName(idx, ui.selected)}
          <button type="button" aria-label="Noņemt izvēli" onClick={() => setUi({ selected: null })}>
            ×
          </button>
        </span>
      ) : null}
      {snap ? (
        <span className="chipx">
          <span className="gh" />
          Salīdzina ar {fmtDate(snap.ts)}
          <button type="button" aria-label="Beigt salīdzināt" onClick={() => setSettings({ compare: null })}>
            ×
          </button>
        </span>
      ) : null}
    </div>
  );
}

function Battery() {
  const en = useApp((s) => s.en!);
  const setTab = useApp((s) => s.setTab);
  const L = en.L;
  const pct = en.pct;
  return (
    <button
      type="button"
      className={'batt' + (pct < 20 ? ' low' : '')}
      aria-label="Enerģijas baterija — atvērt"
      title={'Enerģija tagad ' + Math.round(L) + ' no ' + Math.round(en.M.cap) + ' v. (' + Math.round(pct) + '%) — ' + eStatus(pct) + '. Klikšķini, lai atvērtu.'}
      onClick={() => setTab('energy')}
      data-testid="battery"
    >
      <span className="batt-icon">
        <span className="batt-fill" style={{ height: pct + '%', background: eColor(pct) }} />
        {!en.awake ? (
          <span className="batt-bolt">
            <IconBolt />
          </span>
        ) : null}
      </span>
      <span className="batt-txt">
        <b data-testid="battery-pct">
          {Math.round(L)}
          <em>/{Math.round(en.M.cap)}</em>
        </b>
        <small>{en.awake ? 'vakarā ~' + Math.round(en.atEnd) + ' v.' : 'lādējas'}</small>
      </span>
    </button>
  );
}

function Legend() {
  const view = useApp((s) => s.wheel!.settings.view);
  const compare = useApp((s) => s.wheel!.settings.compare);
  return (
    <div className="legend">
      <span>
        <svg width="28" height="10" aria-hidden="true">
          <line x1="1" y1="5" x2="20" y2="5" style={{ stroke: 'var(--ink-2)' }} strokeWidth="2.5" strokeLinecap="round" />
          <path d="M19 1.5L27 5L19 8.5z" style={{ fill: 'var(--ink-2)' }} />
        </svg>
        dod
      </span>
      <span>
        <svg width="28" height="10" aria-hidden="true">
          <line x1="1" y1="5" x2="20" y2="5" style={{ stroke: 'var(--danger)' }} strokeWidth="2.5" strokeDasharray="4 3" />
          <path d="M19 1.5L27 5L19 8.5z" style={{ fill: 'var(--danger)' }} />
        </svg>
        atņem
      </span>
      <span>biezums = stiprums</span>
      <span>
        <i className="lg-core" />
        trūkuma zona
      </span>
      <span>
        <i className="lg-avg" />
        vidējais līmenis
      </span>
      {view === 'main' ? (
        <span>
          <i className="lg-heat" />
          ārējā josla — apakšjomas
        </span>
      ) : null}
      {compare ? (
        <span>
          <i className="lg-ghost" />
          salīdzinājums
        </span>
      ) : null}
      <span className="lg-tip">
        <IconLink />
        <span>
          Jauna saite: <b>velc no jomas nosaukuma uz citu jomu</b>
        </span>
      </span>
    </div>
  );
}
